import { useMemo } from "react";
import { Incident } from "./data/monitoring-service";

interface UseIncidentsFilterProps {
  incidents: Incident[];
  searchQuery: string;
  statusFilter: string;
  severityFilter: string;
  sortBy: string;
}

const useIncidentsFilter = ({
  incidents,
  searchQuery,
  statusFilter,
  severityFilter,
  sortBy,
}: UseIncidentsFilterProps) => {
  return useMemo(() => {
    if (!incidents) return [];

    const filtered = incidents.filter((incident: Incident) => {
      // Search filter
      const matchesSearch =
        !searchQuery ||
        incident.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        incident.monitorName
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        incident.description.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && !incident.isResolved) ||
        (statusFilter === "resolved" && incident.isResolved);

      // Severity filter
      const matchesSeverity =
        severityFilter === "all" ||
        (severityFilter === "critical" &&
          incident.incidentType === "critical") ||
        (severityFilter === "warning" && incident.incidentType !== "critical");

      return matchesSearch && matchesStatus && matchesSeverity;
    });

    // Sort
    filtered.sort((a: Incident, b: Incident) => {
      if (sortBy === "oldest") {
        return (
          new Date(a.firstDetectedDate).getTime() -
          new Date(b.firstDetectedDate).getTime()
        );
      }
      if (sortBy === "newest") {
        return (
          new Date(b.firstDetectedDate).getTime() -
          new Date(a.firstDetectedDate).getTime()
        );
      }
      if (sortBy === "severity") {
        if (a.isResolved !== b.isResolved) {
          return a.isResolved ? 1 : -1;
        }
        if (a.incidentType === "critical" && b.incidentType !== "critical") {
          return -1;
        }
        if (a.incidentType !== "critical" && b.incidentType === "critical") {
          return 1;
        }
      }
      return 0;
    });

    return filtered;
  }, [incidents, searchQuery, statusFilter, severityFilter, sortBy]);
};

export default useIncidentsFilter;
